// CTW-DURMAH-STUDENT-MANUAL-01: Planning, Lexicon, wellbeing, community,
// account, integrity and help sections.

import type { ManualSection } from "../types";

export const planningAndTrustSections: ManualSection[] = [
  /* ---------------------------------------------------------------- */
  /* Planning and revision                                             */
  /* ---------------------------------------------------------------- */

  {
    slug: "year-at-a-glance",
    title: "Year at a Glance: your real calendar",
    summary:
      "Where your academic year actually lives. Assignments, assessments, timetable, personal items and your university's term structure, merged into one timeline.",
    category: "planning",
    audience: "public",
    order: 1,
    readTime: "3 min read",
    tags: ["Planning", "Calendar"],
    keywords: [
      "yaag",
      "year at a glance",
      "calendar",
      "timetable",
      "deadlines",
      "term dates",
      "exam dates",
      "planner",
      "schedule",
      "import calendar",
      "ics",
    ],
    blocks: [
      {
        kind: "para",
        text: "Year at a Glance merges everything into a single timeline: your assignments, your assessments, your timetable events, your personal items and your university's academic pack — teaching weeks, term dates and provisional exam windows. Where two sources describe the same thing, it keeps the more authoritative one, so you never see duplicates.",
      },
      {
        kind: "bullets",
        intro: "Four views, plus dedicated day, week and month screens:",
        items: [
          "Year — the whole shape of your degree year, with Today Focus, a daily progress tracker and your study streak.",
          "Term — the current term in context.",
          "Month — the traditional grid.",
          "Week — what is actually coming.",
        ],
      },
      {
        kind: "heading",
        id: "filling-it",
        text: "Getting your year in",
      },
      {
        kind: "steps",
        items: [
          "Add items manually with the floating plus button — either a personal item or an assignment.",
          "Upload an .ics calendar file.",
          "Run the sync wizard with your timetable and virtual-learning-environment links.",
        ],
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "It will not show you fake exams",
        text: "For a brand-new account with no real data, Year at a Glance hides provisional exam windows entirely rather than showing placeholder dates that might mislead you into revising for the wrong week.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Do this in week one",
        text: "Importing your timetable is the single change that makes your dashboard genuinely useful, because it is what lets Durmah tell you what matters today instead of guessing.",
      },
    ],
    related: ["dashboard-tour", "my-modules", "exam-prep"],
  },

  {
    slug: "my-modules",
    title: "My Modules and SyllabusShield™",
    summary:
      "Your degree structure, and the coverage tracking that turns 'I think I have missed something' into a specific list of what you have not covered.",
    category: "planning",
    audience: "public",
    order: 2,
    readTime: "3 min read",
    tags: ["Planning", "Modules"],
    keywords: [
      "modules",
      "my modules",
      "syllabus",
      "syllabusshield",
      "coverage",
      "what have i missed",
      "add a module",
      "topics",
      "module code",
    ],
    blocks: [
      {
        kind: "para",
        text: "Each module card shows the code, title, teaching staff, and how many lectures and assignments you have against it. Adding your modules is the highest-value setup step in the whole product — most of Durmah's context awareness flows from it.",
      },
      {
        kind: "steps",
        intro: "Adding a module:",
        items: [
          "Select Add Module and search the catalogue. It is filtered to your year, with an option to show other years.",
          "Add the teaching staff names.",
          "Fill in the SyllabusShield™ topics box — one topic per line. Prefix a line with an exclamation mark to mark it high importance.",
          "Save.",
        ],
      },
      {
        kind: "heading",
        id: "syllabusshield",
        text: "What SyllabusShield™ does",
      },
      {
        kind: "para",
        text: "You list the topics your module actually covers. Durmah then tracks which of those topics your uploaded lectures have covered, and shows you the gaps. Where the catalogue has suggestions, a single button pre-fills them for you.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "The revision use case",
        text: "Three weeks before an exam, coverage tracking tells you which topics you have never uploaded a lecture for. That is usually a much shorter and more alarming list than students expect, and it is the best possible place to start revising.",
      },
    ],
    related: ["year-at-a-glance", "lecture-intelligence", "exam-prep"],
  },

  {
    slug: "exam-prep",
    title: "Exam Prep",
    summary:
      "A revision workspace per module, a readiness and coverage overview, and a four-week revision outline you fill in yourself.",
    category: "planning",
    audience: "public",
    order: 3,
    readTime: "2 min read",
    tags: ["Planning", "Revision"],
    keywords: [
      "exam prep",
      "revision",
      "exam",
      "revise",
      "study plan",
      "readiness",
      "four week plan",
    ],
    blocks: [
      {
        kind: "bullets",
        items: [
          "Exam Overview — a per-module table of readiness and coverage. Select a module to work on it.",
          "Active Workspaces — a workspace per module, with Durmah chat loaded with your exam context.",
          "A four-week revision outline — pick a module and a start date and Durmah generates a weekly scaffold.",
        ],
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "The outline is a scaffold, not an AI study plan",
        text: "Durmah labels it as such, deliberately. A generated 'personalised revision plan' sounds impressive and is usually generic. The scaffold gives you the weeks; you fill in what you actually need, informed by your coverage gaps.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Start from coverage, not from the module list",
        text: "Open SyllabusShield™ coverage first, find the topics with no lecture behind them, and build the four weeks around those. Revising what you already know is comfortable and nearly useless.",
      },
    ],
    related: ["my-modules", "quiz-me", "year-at-a-glance"],
  },

  {
    slug: "quiz-me",
    title: "Quiz Me",
    summary:
      "Active recall on demand, over a lecture, an assignment, a module or a week — in text or out loud.",
    category: "planning",
    audience: "public",
    order: 4,
    readTime: "2 min read",
    tags: ["Planning", "Revision", "Speak Law"],
    keywords: [
      "quiz",
      "quiz me",
      "test me",
      "active recall",
      "practice questions",
      "self test",
      "flashcards",
    ],
    blocks: [
      {
        kind: "table",
        intro: "Setting up a quiz:",
        headers: ["Setting", "Options"],
        rows: [
          ["Mode", "Text or voice"],
          ["Scope", "A lecture, an assignment, a module, or a week"],
          [
            "Style",
            "Quick recall, IRAC, hypothetical, or counter-argument",
          ],
          ["Target", "The specific module, lecture or assignment"],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Counter-argument mode is the one to use",
        text: "It forces you to argue the other side, which is the fastest way to discover that you only half-understand a case. If you can only argue one direction, you do not yet know the material well enough to write about it critically.",
      },
      {
        kind: "para",
        text: "The Quiz Hub lists your recent sessions so you can resume or delete them. Voice quiz sessions save to your transcript library, where you can pick up a quiz again from its transcript.",
      },
      {
        kind: "callout",
        tone: "note",
        title: "There are no flashcards",
        text: "Durmah does not generate flashcard decks from your lectures. Quiz Me is the active-recall tool, and it is deliberately question-led rather than card-led — recall under questioning is closer to what an exam or a seminar actually demands.",
      },
    ],
    related: ["exam-prep", "using-durmah-voice", "durmah-lexicon"],
  },

  {
    slug: "practice-desk",
    title: "Practice Desk",
    summary:
      "An eight-stage simulated legal matter with a fictional client and a supervisor. It teaches the shape of practice, not the substance of a module.",
    category: "planning",
    audience: "public",
    order: 5,
    readTime: "3 min read",
    tags: ["Planning", "Practice"],
    keywords: [
      "practice desk",
      "simulation",
      "client",
      "matter",
      "work experience",
      "training contract",
      "practical skills",
      "attendance note",
    ],
    blocks: [
      {
        kind: "table",
        headers: ["Stage", "What you do"],
        rows: [
          ["1 · First Morning", "Read the supervisor brief and acknowledge the learning boundary."],
          ["2 · Open the Matter", "State the client objective and identify the immediate risks."],
          ["3 · Meet the Client", "Draft your client questions and an attendance note."],
          ["4 · Build the File", "Select the documents you need and build a working chronology."],
          ["5 · Find the Law", "Use verified research sources and write a research note."],
          ["6 · Prepare the Work", "Write an internal case note and a client update."],
          ["7 · Ready the Hearing", "Set your hearing priorities and name your uncertainties."],
          ["8 · Supervisor Review", "Reflect, confirm the work is your own judgement, and tag the skills you practised."],
        ],
      },
      {
        kind: "para",
        text: "Your work autosaves as you go. A Practice File side panel holds your documents, and you can reset the matter and start again.",
      },
      {
        kind: "callout",
        tone: "safety",
        title: "A fictional training exercise",
        text: "Practice Desk is not legal advice, not employment, not qualifying work experience, not accreditation, and not a professional qualification. It exists to teach you the shape of a matter — what a chronology is for, why an attendance note matters, how uncertainty gets escalated to a supervisor.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Worth doing before an application",
        text: "Stages 3 and 8 in particular give you something concrete to talk about at interview: a client question you chose to ask and why, and an uncertainty you correctly escalated rather than guessing at.",
      },
    ],
    related: ["research-hub", "quiz-me", "three-pillars"],
  },

  {
    slug: "research-hub",
    title: "Research Hub",
    summary:
      "Finding authority properly — with a research method, verified authorities, and unverified leads clearly separated from them.",
    category: "planning",
    audience: "public",
    order: 6,
    readTime: "3 min read",
    tags: ["Planning", "Research"],
    keywords: [
      "research",
      "find cases",
      "authorities",
      "case law",
      "bailii",
      "westlaw",
      "legislation",
      "how do i research",
      "find a case",
    ],
    blocks: [
      {
        kind: "steps",
        intro: "The playbook the page opens with — a dependable legal research method:",
        items: [
          "Frame the question.",
          "Find the authority.",
          "Check its status and how it has been treated since.",
          "Keep a research trail.",
          "Read it before you cite it.",
        ],
      },
      {
        kind: "para",
        text: "Type a topic, or use a quick-search suggestion, and Durmah returns the key legal issues, a source verification note, and the verified authorities — each with its role, citation, source link and the principle it carries.",
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "Verified and unverified are kept apart",
        text: "Unverified research leads appear in a separate, clearly-marked panel. They are starting points, not authority. The distinction is the whole point of the page: a leading case and something that merely mentions your topic are not the same kind of thing, and a tool that blurs them is teaching you a bad habit.",
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Coming from an assignment",
        text: "Open Research Hub from inside an assignment and it prefills your query and offers to save the authorities straight into your assignment's research notes, then returns you to the workflow where you left it.",
      },
    ],
    related: ["source-pack-and-argument-map", "assignment-workflow", "durmah-lexicon"],
  },

  {
    slug: "tasks-and-journal",
    title: "Tasks and your study journal",
    summary:
      "Two small, private tools — a task list that surfaces on your dashboard, and a journal for reflections and supervision notes.",
    category: "planning",
    audience: "member",
    order: 7,
    readTime: "1 min read",
    tags: ["Planning", "Member"],
    keywords: ["tasks", "to do", "todo", "journal", "notes", "reflections", "diary"],
    blocks: [
      {
        kind: "bullets",
        items: [
          "My Tasks — open and completed counts, filters, add a task with an optional due date, click to complete, delete with confirmation. Also surfaced as Today's Tasks on your dashboard.",
          "My Journal — a private study journal. Write an entry, save it, and search back through them. Useful for supervision notes and for recording what you actually understood this week.",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "The journal entry worth writing",
        text: "After a seminar, write down the one thing you could not answer. Reading those back before an exam is uncomfortable and extremely efficient.",
      },
    ],
    related: ["dashboard-tour", "year-at-a-glance", "saving-transcripts"],
  },

  /* ---------------------------------------------------------------- */
  /* Lexicon                                                           */
  /* ---------------------------------------------------------------- */

  {
    slug: "durmah-lexicon",
    title: "The Durmah Lexicon™",
    summary:
      "Legal vocabulary is not decoration — it is the medium. The Lexicon is a spaced-recall system for the words your own lectures actually used.",
    category: "lexicon",
    audience: "public",
    order: 1,
    readTime: "4 min read",
    tags: ["Lexicon", "Learn Law"],
    keywords: [
      "lexicon",
      "glossary",
      "legal terms",
      "vocabulary",
      "definition",
      "what does this word mean",
      "terminology",
      "cmd k",
      "look up a term",
    ],
    blocks: [
      {
        kind: "para",
        text: "A student who cannot use estoppel, ratio, obiter, ultra vires or volenti precisely cannot write precisely either. The Lexicon exists to fix that, and it is built around the terms that appeared in your own lectures rather than a generic dictionary.",
      },
      {
        kind: "heading",
        id: "using-it",
        text: "Three ways in",
      },
      {
        kind: "bullets",
        items: [
          "The header strip — a rotating Word of the Moment sits under the navigation on every page, with quick search, pronunciation and a shortcut to ask Durmah about the term.",
          "Cmd+K or Ctrl+K — opens Lexicon search from anywhere in the app without losing your place. This is the fastest way to check a term mid-sentence.",
          "The full Lexicon page — search, browse A–Z, and filter by your own set or all terms.",
        ],
      },
      {
        kind: "heading",
        id: "term-cards",
        text: "What a term card gives you",
      },
      {
        kind: "bullets",
        items: [
          "The definition, and distinct senses where a term carries more than one.",
          "Source references.",
          "Which of your lectures the term appeared in — click through to the lecture itself.",
          "Your own notes.",
          "Star, priority and mastery controls.",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "The lecture link is the point",
        text: "A term is not an abstraction — it is something a named lecturer used on a specific day in a specific argument. Following that link back to the lecture is usually more instructive than re-reading the definition.",
      },
      {
        kind: "heading",
        id: "adding-terms",
        text: "Adding a term Durmah does not have",
      },
      {
        kind: "steps",
        items: [
          "Search for it. If there is no match, you will be offered Define with Durmah.",
          "Durmah returns a definition and a relevance indicator showing whether the term is legal, borderline or not legally relevant.",
          "Optionally add context — the case where you met it, for instance.",
          "Save it to your Lexicon.",
        ],
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "Terms are checked before they are added",
        text: "Durmah checks a term for legal relevance before saving it, and marks anything borderline or non-legal clearly. When you star a term from a lecture, it confirms the term genuinely appears in that lecture's analysis first — no invented vocabulary enters your Lexicon.",
      },
      {
        kind: "heading",
        id: "drill",
        text: "Drill and mastery",
      },
      {
        kind: "para",
        text: "Star a term to add it to your drill set and give it a personal priority. Mark terms as active, mastered or paused — mastered terms drop out of rotation unless you ask to include them. The rotation weights your own starred terms heavily while still introducing new ones, so you keep meeting the words you chose to learn.",
      },
    ],
    related: ["lecture-intelligence", "quiz-me", "talking-to-durmah"],
  },

  /* ---------------------------------------------------------------- */
  /* Wellbeing                                                         */
  /* ---------------------------------------------------------------- */

  {
    slug: "wellbeing",
    title: "Wellbeing",
    summary:
      "A daily check-in, your trends over time, and a coach designed for the humane next step rather than another study plan.",
    category: "wellbeing",
    audience: "public",
    order: 1,
    readTime: "2 min read",
    tags: ["Wellbeing"],
    keywords: [
      "wellbeing",
      "mental health",
      "stress",
      "burnout",
      "mood",
      "check in",
      "sleep",
      "overwhelmed",
      "struggling",
      "anxious",
    ],
    blocks: [
      {
        kind: "para",
        text: "Small resets that protect focus, sleep and confidence — especially during heavy weeks. The wellbeing page carries a standing badge: confidential, and an AI support tool rather than a clinical service.",
      },
      {
        kind: "bullets",
        items: [
          "Daily check-in — a quick mood, stress and sleep record with an optional note.",
          "Your trends — how those measures have moved over time.",
          "Wellbeing Coach — a conversation designed for the humane next step, not another study plan.",
        ],
      },
      {
        kind: "callout",
        tone: "safety",
        title: "Durmah is not a clinical service",
        text: "It is an AI support tool. It does not diagnose, it does not treat, and it is not a substitute for your university's welfare service, your GP, or a trained human. If something feels unsafe, please use the resources on the urgent support page first.",
      },
    ],
    related: ["urgent-support", "privacy-and-your-data", "getting-help"],
  },

  {
    slug: "urgent-support",
    title: "If you need urgent help",
    summary:
      "Free lines, staffed by real people, safe to use at any hour. Durmah is an AI and is not a substitute for any of them.",
    category: "wellbeing",
    audience: "public",
    order: 2,
    readTime: "1 min read",
    tags: ["Wellbeing", "Safety"],
    keywords: [
      "urgent",
      "crisis",
      "emergency",
      "help now",
      "samaritans",
      "suicide",
      "self harm",
      "someone to talk to",
      "welfare",
      "nightline",
    ],
    blocks: [
      {
        kind: "callout",
        tone: "safety",
        title: "Reach a real human",
        text: "Durmah is an AI. It is not a substitute for a trained human right now. The lines below are free to call, staffed by real people, and safe to use at any hour.",
      },
      {
        kind: "table",
        headers: ["Service", "Contact"],
        rows: [
          ["Emergency services", "999"],
          [
            "Samaritans",
            "Call 116 123 free, 24/7, to speak with a volunteer.",
          ],
          ["NHS 111", "Call 111 and choose the mental-health option"],
          ["Nightline", "nightline.ac.uk — run by students, for students"],
          ["Togetherall", "togetherall.com — anonymous peer support, 24/7"],
          ["Durham University Counselling and Mental Health Service", "0191 334 2200"],
          ["Oxford University welfare", "01865 270 300"],
          ["Cambridge University welfare", "01223 332 865"],
        ],
      },
      {
        kind: "callout",
        tone: "safety",
        text: "Durmah never replaces these services. If anything on your mind today feels unsafe, please reach one of them first.",
      },
    ],
    related: ["wellbeing", "getting-help"],
  },

  /* ---------------------------------------------------------------- */
  /* Community                                                         */
  /* ---------------------------------------------------------------- */

  {
    slug: "community",
    title: "Community Hub, the Lounge, and referrals",
    summary:
      "Your university and city guide, the student social space, and how inviting a friend works.",
    category: "community",
    audience: "public",
    order: 1,
    readTime: "2 min read",
    tags: ["Community"],
    keywords: [
      "community",
      "lounge",
      "other students",
      "social",
      "meet people",
      "refer a friend",
      "invite",
      "shoutouts",
    ],
    blocks: [
      {
        kind: "bullets",
        items: [
          "Community Hub — your university and city guide, verified for your institution: practical, local and useful.",
          "Premier Lounge — the student social space. A feed, a member directory with messaging, a shoutouts wall and a virtual coffee table. It requires a community code.",
          "Community recommendations — submit a place or resource for review; approved ones appear in the guide.",
        ],
      },
      {
        kind: "heading",
        id: "referrals",
        text: "Referring a friend",
      },
      {
        kind: "para",
        text: "You can invite other students using their university email addresses. Each invitee receives an email with a redemption link, and when they join they receive a trial.",
      },
      {
        kind: "callout",
        tone: "note",
        title: "Be straight with the people you invite",
        text: "The trial benefit goes to them, not to you. Durmah does not pay students to recruit other students, and you should not tell your friends otherwise.",
      },
    ],
    related: ["getting-access", "wellbeing", "getting-help"],
  },

  /* ---------------------------------------------------------------- */
  /* Account                                                           */
  /* ---------------------------------------------------------------- */

  {
    slug: "billing-and-account",
    title: "Your profile, settings and billing",
    summary:
      "Every setting you can change and where it lives — including the three that are not where you would expect.",
    category: "account",
    audience: "member",
    order: 1,
    readTime: "3 min read",
    tags: ["Account", "Member"],
    keywords: [
      "settings",
      "profile",
      "change my year",
      "avatar",
      "voice settings",
      "billing",
      "invoice",
      "cancel subscription",
      "payment",
      "parent payment",
      "usage",
    ],
    blocks: [
      {
        kind: "table",
        intro: "Profile — your academic identity:",
        headers: ["Setting", "Notes"],
        rows: [
          ["Display name", ""],
          [
            "Year of study",
            "Change it here if you progress mid-subscription. Everything adapts.",
          ],
          ["Degree type", "LLB or MLaw"],
          ["Current modules", "A free-text list"],
          [
            "Session and security",
            "Sign out this device, or sign out all devices",
          ],
        ],
      },
      {
        kind: "table",
        intro: "Settings — how Durmah behaves:",
        headers: ["Setting", "Options"],
        rows: [
          ["Full name and display name", ""],
          ["Avatar", "Image upload"],
          ["Email", "Read-only — it comes from your Google account"],
          ["Durmah voice", "Voice choice with a preview"],
          ["Voice mode", "On or off"],
          ["Voice playback speed", "Slow, Normal or Fast"],
          [
            "AI safety level",
            "Conservative, Balanced or Exploratory",
          ],
          ["Feedback reminders", "Opt in or out"],
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "Three settings that live elsewhere",
        text: "The deadline countdown is toggled by tapping the countdown on your dashboard. The legal news digest email is opted into from the news digest page. Browser reminders are enabled from the sticky notes widget on your dashboard. We know these are not where you would look — they are on the list to move.",
      },
      {
        kind: "heading",
        id: "billing",
        text: "Billing",
      },
      {
        kind: "bullets",
        items: [
          "Overview — your current status, including trial days remaining.",
          "Plans and pricing — change plan; checkout runs through Stripe and accepts promotional codes.",
          "Usage — AI chat messages and voice minutes used.",
          "Billing history — your invoices.",
          "Manage billing — the Stripe portal, where you can update your card, download invoices and cancel.",
          "Share payment link — generates a secure link you can send to a parent so they can pay without needing a Durmah account. It expires after seven days and works once.",
        ],
      },
    ],
    related: ["plans-and-trial", "privacy-and-your-data", "signing-in"],
  },

  {
    slug: "privacy-and-your-data",
    title: "Your data and your privacy",
    summary:
      "What is stored, what is deliberately never stored, and how to request deletion.",
    category: "account",
    audience: "public",
    order: 2,
    readTime: "2 min read",
    tags: ["Account", "Privacy"],
    keywords: [
      "privacy",
      "data",
      "delete my data",
      "gdpr",
      "is my essay private",
      "who can see my work",
      "data protection",
      "deletion",
    ],
    blocks: [
      {
        kind: "bullets",
        intro: "The things worth knowing:",
        items: [
          "Saved academic content is associated with your study account. The privacy policy explains service providers and necessary operational access.",
          "Your workspace shows current AI, voice and upload availability for your account. Use each feature only within its allowances and the permissions for your task and material.",
          "Keep university passwords, payment credentials and sensitive personal information out of prompts and uploads. Automated checks can miss information.",
          "Check the feature's stated storage and processing before entering material. A private account does not mean device-only processing or permission to upload restricted resources.",
          "Memory controls and formal deletion requests cover different records. Do not assume clearing a chat deletes all copies, backups or provider-held data; consult the privacy policy and your request status.",
        ],
      },
      {
        kind: "heading",
        id: "deletion",
        text: "Requesting deletion",
      },
      {
        kind: "para",
        text: "From the privacy page in your account you can request deletion of your uploaded study data, your study workspace data, or your account-related application data. Each request goes into a reviewed queue with an audit trail, and you can add a message if there is anything the team should know.",
      },
      {
        kind: "callout",
        tone: "note",
        title: "The full position",
        text: "The privacy policy is authoritative, with related detail in the student content policy and the university pack data governance policy. All three are public and linked from the footer of every page.",
      },
    ],
    related: ["what-durmah-remembers", "billing-and-account", "getting-help"],
  },

  /* ---------------------------------------------------------------- */
  /* Integrity                                                         */
  /* ---------------------------------------------------------------- */

  {
    slug: "academic-integrity-boundary",
    title: "Academic integrity: the boundary, stated plainly",
    summary:
      "Check the current rules for your task. This guide explains Durmah's limits and your next steps.",
    category: "integrity",
    audience: "public",
    order: 1,
    readTime: "4 min read",
    tags: ["Integrity", "Important"],
    keywords: [
      "academic integrity",
      "plagiarism",
      "cheating",
      "misconduct",
      "can durmah write my essay",
      "will i get caught",
      "ai declaration",
      "declare ai use",
      "is this allowed",
      "turnitin",
    ],
    blocks: [
      {
        kind: "para",
        text: "Durmah supports students in developing their own legal understanding and reasoning. Your workspace shows current AI and upload availability. The forms of assistance below depend on feature availability, material rights and the current rules for your specific task.",
      },
      {
        kind: "heading",
        id: "will-do",
        text: "Support that requires permission and availability",
      },
      {
        kind: "bullets",
        items: [
          "Explain doctrine, cases, statutes and principles.",
          "Interrogate your understanding and tell you where it is thin.",
          "Analyse what a question is actually asking.",
          "Check and challenge your structure.",
          "Name relevant authorities and explain what work each one does.",
          "Read your own draft and tell you, specifically, what to fix.",
          "Help you cite correctly using OSCOLA.",
          "Help you plan revision and manage your academic year.",
          "Tell you honestly when it does not know.",
        ],
      },
      {
        kind: "heading",
        id: "will-not-do",
        text: "What Durmah will not do",
      },
      {
        kind: "bullets",
        items: [
          "Write, draft or rewrite prose for work you will submit.",
          "Produce a model answer for you to adapt.",
          "Predict or promise a mark or classification.",
          "Invent a case, statute, citation, ratio, judge or paragraph reference.",
          "Present unverified material as grounded guidance.",
          "Help you evade detection.",
          "Give legal advice.",
        ],
      },
      {
        kind: "callout",
        tone: "boundary",
        title: "Current controls and their limits",
        text: "Server checks restrict AI use when task permission or release evidence is missing, expired or revoked. Reviewed assessment records must match the student, module, assessment and requested action. These checks do not establish that every university rule has been obtained or correctly interpreted, and do not guarantee that every misuse can be detected.",
      },
      {
        kind: "heading",
        id: "your-obligations",
        text: "Your obligations, in order",
      },
      {
        kind: "steps",
        items: [
          "Read the current university, school, programme, module and assessment instructions. If their scope or priority is unclear, ask your module team before using AI.",
          "Check whether the particular action is permitted, including planning, feedback, editing, research and external processing. Describing work as practice does not override an applicable restriction.",
          "Where disclosure is required, give an accurate account in the required format. Durmah's activity record may be incomplete and cannot certify authorship or permission.",
          "Develop your own reasoning. Writing in your own words or paraphrasing output does not make a prohibited use acceptable.",
          "Verify every citation against your module materials and primary sources before you rely on it.",
          "Keep the reasoning yours. If you cannot explain why an authority supports your point, it does not belong in your answer.",
        ],
      },
      {
        kind: "callout",
        tone: "safety",
        title: "Policy updates need current evidence",
        text: "Durmah does not currently operate automatic monitoring of university websites or private student hubs. Its recorded-policy checks depend on current evidence being reviewed and entered. Do not carry a previous academic year's permission into a new task without checking. Durmah does not claim to have verified your institution's current policy. The public AI Usage Policy explains these limits; no university-wide compatibility or endorsement is claimed.",
      },
    ],
    related: ["assignment-workflow", "mark-my-paper", "what-durmah-is"],
  },

  /* ---------------------------------------------------------------- */
  /* Help                                                              */
  /* ---------------------------------------------------------------- */

  {
    slug: "common-questions",
    title: "Common questions",
    summary:
      "The questions students ask most in their first week, answered directly.",
    category: "help",
    audience: "public",
    order: 1,
    readTime: "3 min read",
    tags: ["Help", "FAQ"],
    keywords: [
      "faq",
      "questions",
      "help",
      "common problems",
      "do i need a credit card",
      "which universities",
      "is my data safe",
      "can durmah write my essay",
    ],
    blocks: [
      {
        kind: "table",
        headers: ["Question", "Answer"],
        rows: [
          [
            "Do I need a credit card to try Durmah?",
            "No. No payment method is collected for the trial and you are never charged automatically.",
          ],
          [
            "Which universities are supported?",
            "Durmah provides a shared independent workspace for UK law students and appropriate adult independent learners. You can add your own university, modules and study context; this does not mean that your institution endorses the service or that every course or jurisdiction is covered. Check current feature availability and your official course instructions.",
          ],
          [
            "Will Durmah write my coursework for me?",
            "Durmah is not intended to write coursework for submission. AI assistance depends on current feature availability and permission for the particular task; writing the final text yourself does not make a prohibited use acceptable.",
          ],
          [
            "Will Durmah tell me what grade I will get?",
            "No. Feedback is diagnostic, with no predicted mark or classification. Your result depends on your university's rubric, your marker and your final submitted work.",
          ],
          [
            "Is my work private?",
            "Saved academic content is associated with your study account. Private access does not mean device-only processing or no operational access. Your workspace shows current AI, voice and upload availability. Read the privacy policy for service providers, retention and deletion requests.",
          ],
          [
            "Do I need a password?",
            "No. Sign-in runs through Google, so there is no Durmah password to create or forget.",
          ],
          [
            "What if my transcript is short or my file will not upload?",
            "Durmah tells you exactly what is wrong and what to do instead. Short transcripts are flagged rather than analysed unreliably.",
          ],
          [
            "Is Durmah affiliated with my university?",
            "No. It is an independent platform. Where Durmah and your module handbook disagree, your handbook wins.",
          ],
        ],
      },
    ],
    related: ["getting-help", "what-durmah-is", "academic-integrity-boundary"],
  },

  {
    slug: "getting-help",
    title: "Getting help",
    summary:
      "Where to go when the manual has not answered your question — and what to include so it gets fixed quickly.",
    category: "help",
    audience: "public",
    order: 2,
    readTime: "2 min read",
    tags: ["Help", "Support"],
    keywords: [
      "support",
      "contact",
      "help",
      "report a bug",
      "something is broken",
      "feedback",
      "who do i ask",
      "problem",
    ],
    blocks: [
      {
        kind: "bullets",
        intro: "Four routes, depending on what you need:",
        items: [
          "Ask Durmah. It knows this manual and can point you to the right section — including by voice.",
          "Support — start here for anything you cannot solve yourself.",
          "Share Feedback — the lightbulb button on any page, or the feedback link. It is read by the product team.",
          "Contact — for general enquiries, and for anything about your account.",
        ],
      },
      {
        kind: "callout",
        tone: "tip",
        title: "What to include in a bug report",
        text: "The page you were on, what you were trying to do, what you expected, what actually happened, the exact wording of any message, and your account email. Including those roughly halves the time to a fix — a report saying 'the upload is broken' cannot be acted on.",
      },
      {
        kind: "callout",
        tone: "note",
        title: "The manual is maintained",
        text: "If something in this manual is wrong, out of date, or unclear, tell us through Share Feedback and say which section. Documentation errors are treated as product bugs.",
      },
    ],
    related: ["common-questions", "urgent-support", "what-durmah-is"],
  },
];
